import { useEffect, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Sparkles, Heart, Info } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "How should I respond to this Airbnb guest complaint?",
  "What should I charge for co-hosting management?",
  "How do I approach this property owner?",
  "How should I handle this bad review?",
  "How do I fill these empty nights?",
  "What should I say in this Airbnb claim?",
];

export default function AskHostHustle() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi — I'm Ask Host Hustle, Kristy's AI coach. Tell me exactly what's going on with your Airbnb business and I'll help you fix it. What are we working on today?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    if (!user) return nav("/login");
    const q = (text ?? input).trim();
    if (!q) return;
    const history = messages;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai-coach/chat", { message: q, history, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.response }]);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Coach is offline. Try again.";
      toast.error(msg);
      setMessages((m) => [...m, { role: "assistant", content: msg }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-brand-alabaster min-h-screen">
      <Navbar />
      <section className="py-12 max-w-4xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-pink/15">
            <Heart className="w-4 h-4 text-brand-pink" fill="#E91E63" />
          </span>
          <div className="uppercase tracking-[0.28em] text-xs text-brand-pink">Ask Host Hustle</div>
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight">Kristy's <span className="italic text-brand-pink">AI Coach</span>, on call 24/7.</h1>
        <p className="mt-4 text-neutral-600 max-w-2xl">Ask anything — owner outreach, guest replies, pricing decisions, claim strategy, difficult conversations. Trained on Host Hustle frameworks.</p>

        <div className="mt-8 rounded-3xl bg-white border border-neutral-100 overflow-hidden">
          <div ref={listRef} data-testid="coach-messages" className="p-6 h-[520px] overflow-y-auto space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="w-9 h-9 rounded-full bg-brand-pink/15 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-brand-pink" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-3xl px-5 py-3 whitespace-pre-wrap text-sm leading-relaxed ${m.role === "user" ? "bg-brand-pink text-white rounded-br-md" : "bg-neutral-100 text-neutral-800 rounded-bl-md"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-brand-pink/15 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-brand-pink animate-pulse" />
                </div>
                <div className="rounded-3xl bg-neutral-100 px-5 py-3 text-sm text-neutral-500">Thinking…</div>
              </div>
            )}
          </div>

          {messages.length <= 1 && (
            <div className="px-6 pb-4 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid={`coach-suggest-${s.slice(0, 20)}`} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-brand-blush text-brand-pink hover:bg-brand-pink hover:text-white transition">
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="border-t border-neutral-100 p-4 flex items-end gap-3">
            <Textarea
              data-testid="coach-input"
              rows={1}
              placeholder="Type your question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              className="flex-1 rounded-2xl border-neutral-200 resize-none min-h-[48px] max-h-40"
            />
            <Button onClick={() => send()} disabled={loading || !input.trim()} data-testid="coach-send-btn" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white h-12 px-6">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-neutral-500">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>AI responses are educational. Verify legal, tax, insurance, and Airbnb-platform specifics with a qualified professional. Host Hustle is an independent educational company and is not affiliated with or endorsed by Airbnb, Inc.</span>
        </div>
      </section>
      <Footer />
    </div>
  );
}
